/**
 * 릴리즈 노트 데이터
 *
 * 새 버전 배포 시 변경사항을 기록합니다.
 * 배열에 항목이 존재하는 버전만 What's New 모달이 자동 표시됩니다.
 *
 * 관리 방법:
 *   1. RELEASE_NOTES 배열의 맨 앞에 새 항목을 추가합니다.
 *   2. npm run extract 실행하여 번역 키를 추출합니다.
 *   3. ko/ja messages.po에 번역을 추가합니다.
 *
 * 주의: version 필드는 package.json과 정확히 매칭할 필요 없습니다.
 *       normalizeVersion()으로 앞 3~5자리를 비교합니다.
 */

import { msg } from '@lingui/macro';
import { MessageDescriptor } from '@lingui/core';

export type ReleaseNoteType = 'feature' | 'improvement' | 'bugfix';

export interface ReleaseNoteItem {
  type: ReleaseNoteType;
  title: MessageDescriptor;
  description?: MessageDescriptor;
}

export interface ReleaseNote {
  /** 버전 문자열 (앞 5자리 기준 비교, 예: '1.3.0.2.7') */
  version: string;
  /** 릴리즈 날짜 YYYY-MM-DD */
  date: string;
  /** 변경 사항 목록 */
  items: ReleaseNoteItem[];
}

/**
 * 버전 문자열을 정규화하여 비교 가능하게 변환.
 * '1.3.0.2.6.7' → '1.3.0.2.6' (앞 5자리)
 */
export function normalizeVersion(version: string): string {
  return version.split('.').slice(0, 5).join('.');
}

/**
 * 특정 버전에 해당하는 릴리즈 노트를 찾습니다.
 */
export function findReleaseNote(version: string): ReleaseNote | undefined {
  const normalized = normalizeVersion(version);
  return RELEASE_NOTES.find(rn => normalizeVersion(rn.version) === normalized);
}

// ── 릴리즈 노트 데이터 (최신순) ──────────────────────────────

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: '1.4.0.4.0',
    date: '2026-07-13',
    items: [
      {
        type: 'improvement',
        title: msg`Task Board Enhancements`,
        description: msg`Task cards now open in a focused overlay, support @mentions (including @everyone) in the body and comments, and add a "Review" status with change history. Any channel member can now edit a task.`,
      },
      {
        type: 'improvement',
        title: msg`Smarter Attachment Opening`,
        description: msg`Attachments now open the best way available — in-app viewer first, then browser, then download — and text files open directly in an in-app viewer.`,
      },
      {
        type: 'bugfix',
        title: msg`Muted Channel Badge`,
        description: msg`Fixed an issue where a muted channel could still show an unread badge until the app was reopened.`,
      },
      {
        type: 'feature',
        title: msg`Attachments in Channel Tasks`,
        description: msg`You can now attach files and images to channel tasks, just like on boards and in chat. Attachments now look and behave consistently across chat, boards, tasks, and comments.`,
      },
      {
        type: 'improvement',
        title: msg`Clearer Japanese Text`,
        description: msg`Japanese text now renders in a dedicated font for a cleaner, more consistent look throughout the app.`,
      },
      {
        type: 'improvement',
        title: msg`Stronger Workspace Access Control`,
        description: msg`Workspaces are now private by default. When a workspace limits sign-up to specific email domains, that limit is now enforced everywhere — including channel invite links.`,
      },
      {
        type: 'bugfix',
        title: msg`Login Button No Longer Gets Stuck`,
        description: msg`Fixed an issue where the social login button could stay in a loading state after you canceled sign-in and returned to the login screen.`,
      },
      {
        type: 'bugfix',
        title: msg`React to Any Post or Comment`,
        description: msg`Fixed an issue where emoji reactions could not be added to posts or comments written by others. The @everyone mention now also appears for members when the board policy allows it.`,
      },
      {
        type: 'bugfix',
        title: msg`Calendar Participant Search`,
        description: msg`Fixed participant search when inviting people to a calendar event — you can now find members by name or email.`,
      },
      {
        type: 'bugfix',
        title: msg`Leaving a Channel Keeps You in Your Workspace`,
        description: msg`Fixed an issue where leaving your last channel could move you into a different workspace. Leave and removal messages now show the person's name correctly.`,
      },
      {
        type: 'bugfix',
        title: msg`Customer Inquiry Page and Member Badges`,
        description: msg`Fixed an error shown when opening the customer inquiry page, and corrected the member role badge and its translations.`,
      },
      {
        type: 'feature',
        title: msg`Android Back Button in Tasks`,
        description: msg`On Android, the hardware back button now returns from a task's detail view to the task list instead of leaving the screen.`,
      },
      {
        type: 'improvement',
        title: msg`Easier-to-Close Mobile Menu`,
        description: msg`The left menu on mobile is now narrower, leaving room to tap outside it to close — so it no longer fills the whole screen.`,
      },
    ],
  },
  {
    version: '1.4.0.3.0',
    date: '2026-07-03',
    items: [
      {
        type: 'feature',
        title: msg`Chat Auto-Translation`,
        description: msg`Automatically translate incoming messages into your language. Turn it on per channel — only the messages currently on screen are translated, and results are shared and cached for everyone.`,
      },
      {
        type: 'improvement',
        title: msg`Enhanced Screen Sharing`,
        description: msg`A shared screen now opens in a floating window you can drag, resize, zoom, and pan, with picture-in-picture support.`,
      },
      {
        type: 'improvement',
        title: msg`Workspace Switcher`,
        description: msg`Switching workspaces now opens a single window that lists all your workspaces, channels, and direct messages, with search.`,
      },
      {
        type: 'improvement',
        title: msg`Muted Channels Stay Quiet`,
        description: msg`When you turn off push notifications for a channel, it is now fully silenced. Its messages no longer add to your notification badge, appear in the notification center, or show an unread mark in the channel list — the channel shows a mute icon instead.`,
      },
      {
        type: 'bugfix',
        title: msg`External Notification Setting Respected`,
        description: msg`Turning off external (webhook/API) message notifications for a channel now also keeps those messages out of your notification badge and center, not just push notifications.`,
      },
      {
        type: 'bugfix',
        title: msg`Channel Guest Permission Fixes`,
        description: msg`Fixed an issue where channel members, including guests, could not start a channel task board or create channel events.`,
      },
      {
        type: 'bugfix',
        title: msg`External Share Link Security`,
        description: msg`Fixed an issue where external file share links did not properly enforce their password and expiration protections.`,
      },
    ],
  },
  {
    version: '1.4.0.2.2',
    date: '2026-06-24',
    items: [
      {
        type: 'bugfix',
        title: msg`Board & Channel Fixes`,
        description: msg`Fixed an issue where channel owners could not create boards due to a sync gap.`,
      },
      {
        type: 'bugfix',
        title: msg`Voice Call Microphone Guidance`,
        description: msg`When starting a voice call, a clear message now appears if the microphone is not connected or access is denied, instead of a technical error.`,
      },
      {
        type: 'improvement',
        title: msg`Stability Improvements`,
        description: msg`Updated the Firebase SDK to resolve intermittent app freezing issues reported by some users.`,
      },
    ],
  },
  {
    version: '1.4.0.2.0',
    date: '2026-06-18',
    items: [
      {
        type: 'feature',
        title: msg`Boards`,
        description: msg`Create boards in workspaces and channels to organize information. Choose a type that fits the purpose — general, notice, or wiki — and write posts with file attachments, comments, replies, reactions, and mentions.`,
      },
      {
        type: 'feature',
        title: msg`Calendar`,
        description: msg`Manage schedules at three levels: personal, channel, and workspace-wide. Invite attendees, see their responses, and set your own reminder time for each event.`,
      },
      {
        type: 'feature',
        title: msg`Channel Tasks`,
        description: msg`Track work within a channel using status, assignee, and due date. Any channel member can update a task, and due dates appear on the channel calendar automatically.`,
      },
      {
        type: 'improvement',
        title: msg`Notification Center`,
        description: msg`Notifications are now grouped into channel, board, and calendar tabs so you can find what you need more easily.`,
      },
    ],
  },
  {
    version: '1.4.0.0.0',
    date: '2026-04-23',
    items: [
      {
        type: 'feature',
        title: msg`Past Message Access for New Members`,
        description: msg`Channel owners can now grant new members permission to view messages sent before they joined.`,
      },
      {
        type: 'improvement',
        title: msg`Enhanced Message Viewer`,
        description: msg`- Reply directly from the message viewer in notifications and search results
- Navigate to previous/next messages within the viewer`,
      },
      {
        type: 'improvement',
        title: msg`Improved Loading Experience`,
        description: msg`Replaced loading spinners with skeleton animations in search results and channel message loading.`,
      },
      {
        type: 'bugfix',
        title: msg`Bug Fixes`,
        description: msg`- Fixed an issue where the app would not open from the channel invite screen on mobile
- Fixed login error messages not displaying when entering wrong password
- Fixed PWA push notification delivery failures`,
      },
    ],
  },
  {
    version: '1.3.0.3.0',
    date: '2026-04-14',
    items: [
      {
        type: 'feature',
        title: msg`API Tokens`,
        description: msg`Added API token generation for external service integration.`,
      },
      {
        type: 'feature',
        title: msg`Feedback System`,
        description: msg`- Added in-app feedback and bug report submission feature
- Added edge floating button for quick screen capture
- Added shake-to-capture support on mobile devices`,
      },
      {
        type: 'improvement',
        title: msg`High-Precision AI Meeting Minutes`,
        description: msg`Upgraded to STT V2 model and added speaker diarization feature.`,
      },
      {
        type: 'improvement',
        title: msg`Workspace Switcher Improvements`,
        description: msg`- Added keyboard navigation (Cmd/Ctrl + K, up/down arrows)
- Added channel header long-press support on mobile`,
      },
      {
        type: 'bugfix',
        title: msg`UI & Stability Improvements`,
        description: msg`- Fixed reaction display bugs
- Changed 'Room' terminology to 'Channel'
- Improved English translations
- Fixed duplicate PWA push notifications`,
      },
    ],
  },
];
